import React, { useState } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

interface PWAPromptProps {
  onInstall: () => Promise<boolean>;
  onDismiss: () => void;
  showIOSInstructions?: boolean;
  iosInstructions?: string[];
}

const PWAPrompt: React.FC<PWAPromptProps> = ({ 
  onInstall, 
  onDismiss, 
  showIOSInstructions = false,
  iosInstructions = []
}) => {
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const success = await onInstall();
      if (success) {
        onDismiss();
      }
    } finally {
      setInstalling(false);
    }
  };

  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center p-4 z-50 md:items-center">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <DevicePhoneMobileIcon className="w-6 h-6 text-blue-600" />
              Install ETrax
            </h3>
            <button
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-4">
            <p className="text-gray-600 mb-4">
              Add ETrax to your home screen for quick access and a better experience.
            </p>
            
            <div className="space-y-3">
              {iosInstructions.map((instruction, index) => (
                <div key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <p className="text-sm text-gray-700">{instruction}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex gap-3 p-4 border-t border-gray-200">
            <button
              onClick={onDismiss}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center p-4 z-50 md:items-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ArrowDownTrayIcon className="w-6 h-6 text-blue-600" />
            Install ETrax
          </h3>
          <button
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <DevicePhoneMobileIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Get the full experience</h4>
            <p className="text-gray-600 text-sm">
              Install ETrax on your device for quick access, offline support, and push notifications.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <h5 className="font-medium text-gray-900 mb-2">Features you'll get:</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Work offline and sync when connected</li>
              <li>• Quick access from home screen</li>
              <li>• Push notifications for due dates</li>
              <li>• Native app-like experience</li>
            </ul>
          </div>
        </div>
        
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onDismiss}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Not Now
          </button>
          <button
            onClick={handleInstall}
            disabled={installing}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {installing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Installing...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="w-4 h-4" />
                Install
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAPrompt;